import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useServiceCallViewModel } from "../../../viewmodels/operation/servicecall/useServiceCallViewModel";

import ReportHeader from "../../../components/reports/ReportHeader";

import ReportFooter from "../../../components/reports/ReportFooter";

import EditServiceCallForm from "./EditServiceCallForm";

const ViewServiceCall = () => {
  const { id } = useParams();

  const { getServiceCallById } = useServiceCallViewModel();

  const [serviceCall, setServiceCall] = useState<any>(null);

  const [showEditModal, setShowEditModal] = useState(false);

  // ================= DATE FORMAT =================
  const formatDate = (date: string) => {
    if (!date) return "";

    return new Date(date).toLocaleDateString("en-GB");
  };

  // ================= TIME FORMAT =================
  const formatTime = (time: string) => {
    if (!time) return "";

    return new Date(time).toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ================= LOAD =================
  const loadData = async () => {
    if (!id) return;

    const data = await getServiceCallById(id);

    if (data) {
      setServiceCall(data);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  // ================= PRINT =================
  const handlePrint = () => {
    window.print();
  };

  if (!serviceCall) {
    return (
      <DashboardLayout>
        <div className="p-10">Loading...</div>
      </DashboardLayout>
    );
  }

  /// ================= TECH ROWS =================
  const existingRows = serviceCall?.serviceCallTechnicians || [];

  // DYNAMIC BLANK ROWS
  // keeps everything inside one page
  const blankRowCount =
    existingRows.length === 0 ? 3 : existingRows.length <= 2 ? 2 : 0;

  const technicianRows = [
    ...existingRows.map((x: any) => ({
      ...x,

      hoursWorked: x.hoursWorked === 0 ? "" : x.hoursWorked,

      qty: x.qty === 0 ? "" : x.qty,
    })),

    ...Array.from({
      length: blankRowCount,
    }).map(() => ({
      dateWorked: "",

      hoursWorked: "",

      arrival: "",

      depature: "",

      spareParts: "",

      qty: "",
    })),
  ];

  return (
    <DashboardLayout>
      {/* PRINT STYLE */}
      <style>
        {`

            @page {

            size: A4 portrait;
            margin: 8mm;

            }

            @media print {

              .print-area {
                position: relative;
                height: 277mm;   /* A4 height minus margins */
              }

              .report-footer {
                position: absolute;
                left: 0;
                right: 0;
                bottom: 0;
              }
            }

            @media print {

            html,
            body {

                width: 210mm;
                height: 297mm;

                background: white !important;

                overflow: hidden !important;

            }

            body * {

                visibility: hidden;

            }

            .print-area,
            .print-area * {

                visibility: visible;

            }

            .print-area {

                position: absolute;

                left: 0;
                top: 0;

                width: 100%;

                padding: 0;

                margin: 0;

                background: white;

            }

            

            .print-hide {

                display: none !important;

            }

            }

        `}
      </style>
      <div className="bg-slate-100 min-h-screen p-4 print:bg-white print:p-0">
        {/* PRINT BUTTON */}
        <div className="flex justify-end gap-3 mb-4 print-hide">
          <button
            onClick={() => setShowEditModal(true)}
            className="bg-yellow-600 text-white px-5 py-3 rounded-xl"
          >
            Edit
          </button>

          <button
            onClick={handlePrint}
            className="bg-primary text-white px-5 py-3 rounded-xl"
          >
            Print
          </button>
        </div>

        {/* PAPER */}
        <div className="print-area w-full max-w-[1100px] mx-auto bg-white p-4 text-[12px] leading-tight min-h-[1120px] flex flex-col">
          {/* PAGE CONTENT */}
          <div className="flex flex-col flex-1">
            {/* HEADER */}
            <ReportHeader />

            {/* TOP DETAILS */}
            <div
              className={`grid grid-cols-3 gap-4 mb-2 text-[15px] ${
                serviceCall.customerComment ? "mt-8" : "mt-2"
              }`}
            >
              <div>
                <span className="font-bold">Date:</span>{" "}
                {formatDate(serviceCall.date)}
              </div>

              <div>
                <span className="font-bold">Order No:</span>{" "}
                {serviceCall.orderNumber}
              </div>

              <div>
                <span className="font-bold">No:</span>{" "}
                <span className="text-red-600 font-bold text-[14px]">
                  {serviceCall.no}
                </span>
              </div>
            </div>

            {/* CUSTOMER */}
            <div
              className={`grid grid-cols-2 gap-x-12 text-[15px] ${
                serviceCall.customerComment ? "mt-3 mb-3" : "mt-0 mb-3"
              }`}
            >
              {/* LEFT */}
              <div className="space-y-2 leading-[1.15]">
                <div className="flex items-start gap-2">
                  <span className="font-bold whitespace-nowrap">Company:</span>

                  <span className="break-words">
                    {serviceCall.customerName}
                  </span>
                </div>

                <div className="flex items-start gap-2">
                  <span className="font-bold whitespace-nowrap">Address:</span>

                  <span className="break-words">
                    {serviceCall.customerAddress}
                  </span>
                </div>
              </div>

              {/* RIGHT */}
              <div className="space-y-2 leading-[1.15]">
                <div className="flex items-start gap-2">
                  <span className="font-bold whitespace-nowrap">
                    Contact Name:
                  </span>

                  <span className="break-words">
                    {serviceCall.customerPhoneName}
                  </span>
                </div>

                <div className="flex items-start gap-2">
                  <span className="font-bold whitespace-nowrap">Contact:</span>

                  <span className="break-words">
                    {serviceCall.customerPhoneNumber}
                  </span>
                </div>
              </div>
            </div>
            {/* SCALE DETAILS */}
            <div>
              <div className="font-bold text-[18px] mb-4">Scale Details:</div>

              {serviceCall.serviceCallScales?.map(
                (scale: any, index: number) => (
                  <div key={index} className="grid grid-cols-2 gap-8 mb-4">
                    {/* LEFT */}
                    <div>
                      <div className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          Make:
                        </span>

                        <div
                          className={`flex-1 ml-2 px-1 min-h-[22px] flex items-end ${
                            !scale.make ? "border-b border-black" : ""
                          }`}
                        >
                          {scale.make || "\u00A0"}
                        </div>
                      </div>

                      {/* MODEL */}
                      <div className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          Model:
                        </span>

                        <div
                          className={`flex-1 ml-2 px-1 min-h-[22px] flex items-end ${
                            !scale.model ? "border-b border-black" : ""
                          }`}
                        >
                          {scale.model || "\u00A0"}
                        </div>
                      </div>
                    </div>

                    {/* RIGHT */}
                    <div>
                      {/* CAPACITY */}
                      <div className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          Capacity:
                        </span>

                        <div
                          className={`flex-1 ml-2 px-1 min-h-[22px] flex items-end ${
                            !scale.capacity ? "border-b border-black" : ""
                          }`}
                        >
                          {scale.capacity || "\u00A0"}
                        </div>
                      </div>

                      {/* SERIAL NO */}
                      <div className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          Serial No:
                        </span>

                        <div
                          className={`flex-1 ml-2 px-1 min-h-[22px] flex items-end ${
                            !scale.serialNumber ? "border-b border-black" : ""
                          }`}
                        >
                          {scale.serialNumber || "\u00A0"}
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>

            {/* CUSTOMER COMMENT */}
            <div
              className={`border border-slate-400 p-3 mb-4 ${
                serviceCall.customerComment ? "" : "min-h-[70px]"
              }`}
            >
              {serviceCall.customerComment ? (
                <div className="whitespace-pre-wrap break-words">
                  <span className="font-bold">Customer Comments:</span>

                  <span> {serviceCall.customerComment}</span>
                </div>
              ) : (
                <div>
                  <div className="flex items-end mb-2">
                    <span className="font-bold whitespace-nowrap mr-2">
                      Customer Comments:
                    </span>

                    <div className="flex-1 border-b border-black h-[20px]" />
                  </div>

                  <div className="border-b border-black h-[24px]" />
                </div>
              )}
            </div>

            {/* LOAN / HIRE */}
            <div>
              <div className="font-bold text-[18px] mb-3">
                {serviceCall.loanScale?.length > 0
                  ? serviceCall.loanScale[0].scaleType === 0
                    ? "Loan Scale Details"
                    : serviceCall.loanScale[0].scaleType === 1
                      ? "Hire Scale Details"
                      : "Loan/Hire Scale Details"
                  : "Loan/Hire Scale Details"}
                :
              </div>

              {(serviceCall.loanScale?.length ?? 0) > 0 ? (
                serviceCall.loanScale.map((loan: any, index: number) => (
                  <div key={index} className="grid grid-cols-2 gap-8 mb-4">
                    {/* LEFT */}
                    <div>
                      <div className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          Make:
                        </span>

                        <div
                          className={`flex-1 ml-2 px-1 min-h-[22px] flex items-end ${
                            !loan.make ? "border-b border-black" : ""
                          }`}
                        >
                          {loan.make || "\u00A0"}
                        </div>
                      </div>

                      <div className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          Model:
                        </span>

                        <div
                          className={`flex-1 ml-2 px-1 min-h-[22px] flex items-end ${
                            !loan.model ? "border-b border-black" : ""
                          }`}
                        >
                          {loan.model || "\u00A0"}
                        </div>
                      </div>

                      <div className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          Capacity:
                        </span>

                        <div
                          className={`flex-1 ml-2 px-1 min-h-[22px] flex items-end ${
                            !loan.capacity ? "border-b border-black" : ""
                          }`}
                        >
                          {loan.capacity || "\u00A0"}
                        </div>
                      </div>

                      <div className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          Serial No:
                        </span>

                        <div
                          className={`flex-1 ml-2 px-1 min-h-[22px] flex items-end ${
                            !loan.serialNumber ? "border-b border-black" : ""
                          }`}
                        >
                          {loan.serialNumber || "\u00A0"}
                        </div>
                      </div>
                    </div>

                    {/* RIGHT */}
                    <div>
                      <div className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          Signed:
                        </span>

                        <div
                          className={`flex-1 ml-2 px-1 min-h-[22px] flex items-end ${
                            !loan.signed ? "border-b border-black" : ""
                          }`}
                        >
                          {loan.signed || "\u00A0"}
                        </div>
                      </div>

                      <div className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          Signed Date:
                        </span>

                        <div
                          className={`flex-1 ml-2 px-1 min-h-[22px] flex items-end ${
                            !loan.signedDate ? "border-b border-black" : ""
                          }`}
                        >
                          {loan.signedDate
                            ? formatDate(loan.signedDate)
                            : "\u00A0"}
                        </div>
                      </div>

                      <div className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          Returned:
                        </span>

                        <div
                          className={`flex-1 ml-2 px-1 min-h-[22px] flex items-end ${
                            loan.haveScaleBeenReturned === null ||
                            loan.haveScaleBeenReturned === undefined
                              ? "border-b border-black"
                              : ""
                          }`}
                        >
                          {loan.haveScaleBeenReturned === 1
                            ? "YES"
                            : loan.haveScaleBeenReturned === 0
                              ? "NO"
                              : "\u00A0"}
                        </div>
                      </div>

                      <div className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          Return Date:
                        </span>

                        <div
                          className={`flex-1 ml-2 px-1 min-h-[22px] flex items-end ${
                            !loan.returnDate ? "border-b border-black" : ""
                          }`}
                        >
                          {loan.returnDate
                            ? formatDate(loan.returnDate)
                            : "\u00A0"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="grid grid-cols-2 gap-8 mb-4">
                  <div>
                    {["Make", "Model", "Capacity", "Serial No"].map((label) => (
                      <div key={label} className="mb-3 flex items-end">
                        <span className="font-bold whitespace-nowrap">
                          {label}:
                        </span>

                        <div className="flex-1 ml-2 border-b border-black min-h-[22px]" />
                      </div>
                    ))}
                  </div>

                  <div>
                    {["Signed", "Signed Date", "Returned", "Return Date"].map(
                      (label) => (
                        <div key={label} className="mb-3 flex items-end">
                          <span className="font-bold whitespace-nowrap">
                            {label}:
                          </span>

                          <div className="flex-1 ml-2 border-b border-black min-h-[22px]" />
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* TECH COMMENTS */}
            <div className="border border-slate-400 min-h-[120px] p-3 mb-3">
              {/* TOP ROW */}
              <div className="flex justify-between gap-6 mb-3">
                {/* TECHNICIAN */}
                <div className="flex items-end flex-1">
                  <span className="font-bold whitespace-nowrap mr-2">
                    Technician:
                  </span>

                  {serviceCall.technicianName ? (
                    <span>{serviceCall.technicianName}</span>
                  ) : (
                    <div className="flex-1 border-b border-black h-[20px]" />
                  )}
                </div>

                {/* DATE */}
                <div className="flex items-end min-w-[220px]">
                  <span className="font-bold whitespace-nowrap mr-2">
                    Date:
                  </span>

                  {serviceCall.technicianCommentDate ? (
                    <span>
                      {new Date(
                        serviceCall.technicianCommentDate,
                      ).toLocaleDateString("en-GB")}
                    </span>
                  ) : (
                    <div className="flex-1 border-b border-black h-[20px]" />
                  )}
                </div>
              </div>

              {/* COMMENT */}
              {serviceCall.technicianComment ? (
                <div>
                  <span className="font-bold">Tech. Comments:</span>{" "}
                  {serviceCall.technicianComment}
                </div>
              ) : (
                <div>
                  {/* LINE 1 */}
                  <div className="flex items-end mb-2">
                    <span className="font-bold whitespace-nowrap mr-2">
                      Tech. Comments:
                    </span>

                    <div className="flex-1 border-b border-black h-[20px]" />
                  </div>

                  {/* LINE 2 */}
                  <div className="border-b border-black h-[24px] mb-2" />

                  {/* LINE 3 */}
                  <div className="border-b border-black h-[24px] mb-2" />

                  {/* LINE 4 */}
                  <div className="border-b border-black h-[24px]" />
                </div>
              )}
            </div>

            {/* TECH TABLE */}
            <table className="w-full border-[1px] border-black border-collapse text-[12px]">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border-[1px] border-black p-2">Date</th>

                  <th className="border-[1px] border-black p-2">
                    Hours Worked
                  </th>

                  <th className="border-[1px] border-black p-2">Arrival</th>

                  <th className="border-[1px] border-black p-2">Departure</th>

                  <th className="border-[1px] border-black p-2">Spare Parts</th>

                  <th className="border-[1px] border-black p-2">QTY</th>
                </tr>
              </thead>

              <tbody>
                {technicianRows.map((tech: any, index: number) => (
                  <tr key={index}>
                    <td className="border-[1px] border-black p-2 h-[34px]">
                      {formatDate(tech.dateWorked)}
                    </td>

                    <td className="border-[1px] border-black p-2">
                      {tech.hoursWorked}
                    </td>

                    <td className="border-[1px] border-black p-2">
                      {formatTime(tech.arrival)}
                    </td>

                    <td className="border-[1px] border-black p-2">
                      {formatTime(tech.depature)}
                    </td>

                    <td className="border-[1px] border-black p-2">
                      {tech.spareParts}
                    </td>

                    <td className="border-[1px] border-black p-2">
                      {tech.qty}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* SIGN */}
            <div
              className={`flex justify-between items-center mb-3 ${
                serviceCall.customerComment ? "mt-8" : "mt-5"
              }`}
            >
              <div>
                <div className="font-bold text-[18px]">
                  Customer Signature:______________
                </div>

                <div className="italic text-[12px]">
                  Work carried out satisfactorily
                </div>
              </div>

              <div className="font-bold text-[18px]">Date: ______________</div>
            </div>

            {/* FOOTER */}
            <div className="report-footer">
              <ReportFooter message="All goods remain the property of Universal Weighing until payment made in full. It is the customer's responsibility to use trade approved scales for trading by weight." />
            </div>
          </div>
        </div>
      </div>
      {showEditModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white w-[95vw] h-[95vh] rounded-3xl flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-2xl font-bold">Edit Service Call</h2>

              <button
                onClick={() => setShowEditModal(false)}
                className="bg-red-500 text-white px-4 py-2 rounded-xl"
              >
                Close
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <EditServiceCallForm
                id={id!}
                onClose={() => setShowEditModal(false)}
                onSaved={() => {
                  setShowEditModal(false);
                  loadData();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ViewServiceCall;
